/**
 * Magazijn — slide-over voor "Nieuwe zending registreren" (P1 Hotfix Feb 2026).
 *
 * Verbeteringen t.o.v. eerste versie:
 *  - Volledige NL-terminologie (pakket → zending).
 *  - Service-types uitgebreid met airfreight/seafreight/ground/courier/
 *    shopdrop_ship/counter_sale (alle 6 met Nederlandse labels).
 *  - Multi-colli items[]: gebruiker kan meerdere colli-regels invoeren met
 *    eigen omschrijving/gewicht/afmetingen/waarde/verpakkingstype.
 *  - Live totalen op basis van de items array.
 *  - Klantkoppeling: zoek-veld bovenaan afzender én ontvanger met debounced
 *    /admin/customers?q= lookup; selecteer een klant en NAW wordt automatisch
 *    ingevuld + customer_id wordt op de zending gezet.
 *  - Barcode/QR scanner-knop bij het externe-W-nummer veld.
 *  - Backwards-compat: bestaande pakketten zonder items[] blijven werken
 *    omdat backend de aggregaten automatisch berekent.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Warehouse, Plus, Loader2, CheckCircle2, Circle, Package as PackageIcon,
  User as UserIcon, MapPin, Box, ArrowRight, Sparkles, Printer, Layers,
  Eye, ChevronRight, X as XIcon, AlertCircle, Trash2, Copy, ClipboardCopy,
  Search,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import EnterpriseEntityPicker from "../shared/EnterpriseEntityPicker";
import RecipientAddressBookPicker, { recipientBookToParty } from "../shared/RecipientAddressBookPicker";
import { CustomerSearchField, customerToParty, customerDisplayName,
         calcItemCbm, calcItemVolWeight, computeTotals,
         TRANSPORT_MODE_LABELS, transportModeForService,
         volumetricDivisorForService } from "./WarehouseFormShared";
// P0 SPRINT (Aug 2026) — CANONICAL CUSTOMER AUTOFILL
import { detectAddressOverrides, applyContactToParty } from "../../../services/customerAutofillService";
import AddressOverrideDialog from "../../shared/AddressOverrideDialog";
import ContactPersonSelector from "../../shared/ContactPersonSelector";
import WarehousePreAlertMatch from "./WarehousePreAlertMatch";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../../ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../ui/alert-dialog";

import { adminApi } from "../../../context/AuthContext";
import { ScanButton } from "./ScanButton";

// ============================================================
// Constants
// ============================================================

const SERVICE_TYPES = [
  { key: "airfreight",    label: "Luchtvracht" },
  { key: "seafreight",    label: "Zeevracht" },
  { key: "ground",        label: "Ground / Wegtransport" },
  { key: "courier",       label: "Courier" },
  { key: "shopdrop_ship", label: "ShopDrop&Ship" },
  { key: "counter_sale",  label: "Balieverkoop" },
];

const PACKAGING_TYPES = [
  { key: "box",      label: "Doos" },
  { key: "barrel",   label: "Barrel" },
  { key: "suitcase", label: "Koffer" },
  { key: "pallet",   label: "Pallet" },
  { key: "bag",      label: "Zak" },
  { key: "loose",    label: "Los item" },
  { key: "other",    label: "Anders" },
];

const MEASUREMENT_LABELS = {
  maritimo_cbm: "Maritiem (CBM)",
  maritimo_cf:  "Maritiem (cu.ft)",
  aero_kl:      "Aero (kg)",
  aero_lb:      "Aero (lb)",
  kg_cbm:       "Vracht (kg / CBM)",
  lb_cf:        "Vracht (lb / cu.ft)",
};

// Sprint 3b — 6 Mrt 2026: XCG vervangt ANG voor nieuwe warehouse-pakketten.
import { SUPPORTED_CURRENCIES } from "../../../lib/currencies";
const CURRENCIES = SUPPORTED_CURRENCIES;

// Volumetrische deler: canoniek per transport-mode via WarehouseFormShared
// (spiegel van backend `platform_core/colli.py`). Frontend = alleen preview.
const num = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const fmtNum = (n, digits = 2) =>
  Number.isFinite(n) ? n.toFixed(digits).replace(".", ",") : "0,00";

const newColli = (idx) => ({
  // local id (frontend) — backend kent zijn eigen uuid toe bij opslaan
  _local_id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  colli_number: idx + 1,
  description: "",
  quantity: 1,
  weight_kg: "",
  length_cm: "",
  width_cm: "",
  height_cm: "",
  declared_value: "",
  packaging_type: "box",
});

const EMPTY_PARTY = {
  name: "", company: "", phone: "", whatsapp: "", email: "",
  street: "", house_number: "", house_number_addition: "",
  postal_code: "", city: "", country: "",
};

const EMPTY_FORM = {
  warehouse_hub: "",
  agency: "",
  service_type: "seafreight",
  linked_external_tracking_number: "",
  sender:     { ...EMPTY_PARTY, country: "NL" },
  recipient:  { ...EMPTY_PARTY, country: "Curaçao" },
  customer_id: null,
  // Inhoud (zending-niveau)
  destination: "",
  customer_note: "",
  internal_note: "",
  currency: "EUR",
  measurement: "maritimo_cbm",
  items: [newColli(0)],
};

// ============================================================
// Block status berekenen
// ============================================================

const blockStatus = (form) => {
  const basisDone =
    !!form.warehouse_hub.trim() && !!form.service_type;
  const senderDone =
    !!(form.sender.name || "").trim() &&
    !!((form.sender.phone || "").trim() || (form.sender.whatsapp || "").trim() || (form.sender.email || "").trim());
  const recipientDone =
    !!(form.recipient.name || "").trim();
  const contentDone =
    form.items.length > 0 &&
    form.items.every((it) =>
      num(it.weight_kg) > 0 && num(it.length_cm) > 0 && num(it.width_cm) > 0 && num(it.height_cm) > 0,
    );
  return { basisDone, senderDone, recipientDone, contentDone };
};

// Rekenhelpers komen uit WarehouseFormShared (canonieke deler per dienst).

// ============================================================
// Main component
// ============================================================

export default function WarehousePackageForm({ open, onClose, onCreated, initialPrealertId = null }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [preAlert, setPreAlert] = useState(null);
  const [preAlertConfirmed, setPreAlertConfirmed] = useState(false);
  const clearPreAlert = () => { setPreAlert(null); setPreAlertConfirmed(false); };
  useEffect(() => {
    if (preAlert && form.customer_id !== preAlert.customer_id) clearPreAlert();
  }, [form.customer_id, preAlert]);
  const updateExternalTracking = value => {
    clearPreAlert();
    setForm(f => ({ ...f, linked_external_tracking_number: value.toUpperCase() }));
  };
  const selectPreAlert = async row => {
    setPreAlert(row); setPreAlertConfirmed(false);
    setSaveRecipient(false);
    setSenderSourceCustomer(null); setRecipientSourceCustomer(null);
    setRecipientBookId(null);
    setForm(f => ({ ...f, customer_id: row.customer_id, currency: row.currency,
      linked_external_tracking_number: row.tracking_number || f.linked_external_tracking_number,
      sender: { ...f.sender, name: row.supplier, company: row.supplier },
      recipient: { ...f.recipient, name: row.customer.display_name || row.customer.company_name || `${row.customer.first_name || ""} ${row.customer.last_name || ""}`.trim(),
                   email: row.customer.email || "", phone: row.customer.phone || "",
                   country: row.destination || f.recipient.country },
      customer_note: [row.customer_note, row.invoice_exempt ? `Klantverklaring aankoopfactuur: ${row.invoice_reason}. ${row.invoice_explanation || ""} (geen vrijstelling)` : ""].filter(Boolean).join("\n"),
      items: f.items.map((item, i) => i ? item : { ...item, description: row.contents_description,
        declared_value: f.items.length === 1 && Number(item.quantity) === 1 ? row.value : item.declared_value }),
    }));
    if (row.linked_shipment?.id) {
      try {
        const r = await adminApi.get(`/admin/shipping-labels/${encodeURIComponent(row.linked_shipment.id)}`);
        const label = r.data;
        setForm(f => ({ ...f, sender: { ...f.sender, ...label.sender }, recipient: { ...f.recipient, ...label.recipient },
          warehouse_hub: label.warehouse_hub || f.warehouse_hub,
          service_type: label.service_type || f.service_type, currency: label.currency || f.currency,
          linked_external_tracking_number: label.linked_external_tracking_number || f.linked_external_tracking_number }));
      } catch (e) { toast.error(formatApiError(e)); }
    }
  };
  // P0 SPRINT (Aug 2026) — CANONICAL CUSTOMER AUTOFILL: source-tracking + override dialog
  const [senderSourceCustomer, setSenderSourceCustomer] = useState(null);
  const [recipientSourceCustomer, setRecipientSourceCustomer] = useState(null);
  // Batch 3 · Fase 5 — Actieve contactpersoon per party. "primary" of "contact-{id}".
  // Wordt gereset naar "primary" bij elke NIEUWE klant-selectie zodat er nooit
  // stale contact-selectie blijft hangen van een vorige klant.
  const [senderContactKey, setSenderContactKey] = useState("primary");
  const [recipientContactKey, setRecipientContactKey] = useState("primary");
  const [overrideDialog, setOverrideDialog] = useState(null);
  const [confirmCopyAll, setConfirmCopyAll] = useState(false);
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);
  // Sprint jun 2026 — herbruikbare ontvangers (adresboek van de klant).
  const [recipientBookId, setRecipientBookId] = useState(null);
  const [saveRecipient, setSaveRecipient] = useState(true);
  const firstFieldRef = useRef(null);

  const setSender = (patch) => setForm((f) => ({ ...f, sender: { ...f.sender, ...patch } }));
  const setRecipient = (patch) => setForm((f) => ({ ...f, recipient: { ...f.recipient, ...patch } }));

  const status = useMemo(() => blockStatus(form), [form]);
  const allDone = status.basisDone && status.senderDone && status.recipientDone && status.contentDone;
  const completedBlocks =
    (status.basisDone ? 1 : 0) +
    (status.senderDone ? 1 : 0) +
    (status.recipientDone ? 1 : 0) +
    (status.contentDone ? 1 : 0);

  const totals = useMemo(
    () => computeTotals(form.items, form.service_type),
    [form.items, form.service_type],
  );

  const reset = () => {
    clearPreAlert();
    setForm(EMPTY_FORM);
    setSuccess(null);
    // Batch 3 · Fase 5 — stale-reset ook op form-reset zodat een nieuw
    // pakket geen contact-selectie van een vorig pakket erft.
    setSenderSourceCustomer(null);
    setRecipientSourceCustomer(null);
    setSenderContactKey("primary");
    setRecipientContactKey("primary");
    setRecipientBookId(null);
    setSaveRecipient(true);
    setTimeout(() => firstFieldRef.current?.focus(), 100);
  };

  const handleClose = () => {
    if (busy) return;
    clearPreAlert();
    setForm(EMPTY_FORM);
    setSuccess(null);
    onClose?.();
  };

  // ====== Colli operations ======
  const addColli = () => {
    setForm((f) => ({
      ...f,
      items: [...f.items, newColli(f.items.length)],
    }));
  };

  const copyPrevColli = () => {
    setForm((f) => {
      if (f.items.length === 0) return f;
      const last = f.items[f.items.length - 1];
      const copy = {
        ...newColli(f.items.length),
        description: last.description,
        quantity: last.quantity,
        weight_kg: last.weight_kg,
        length_cm: last.length_cm,
        width_cm: last.width_cm,
        height_cm: last.height_cm,
        declared_value: last.declared_value,
        packaging_type: last.packaging_type,
      };
      return { ...f, items: [...f.items, copy] };
    });
  };

  const copyToAll = () => {
    setForm((f) => {
      if (f.items.length === 0) return f;
      const first = f.items[0];
      const items = f.items.map((it, idx) => ({
        ...it,
        colli_number: idx + 1,
        description: first.description,
        quantity: first.quantity,
        weight_kg: first.weight_kg,
        length_cm: first.length_cm,
        width_cm: first.width_cm,
        height_cm: first.height_cm,
        declared_value: first.declared_value,
        packaging_type: first.packaging_type,
      }));
      return { ...f, items };
    });
    setConfirmCopyAll(false);
    toast.success("Eerste colli is naar alle colli gekopieerd");
  };

  const updateItem = (idx, patch) => {
    setForm((f) => {
      const items = f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
      return { ...f, items };
    });
  };

  const isItemEmpty = (it) =>
    !it.description?.trim() && !it.weight_kg && !it.length_cm && !it.width_cm && !it.height_cm && !it.declared_value;

  const deleteItem = (idx) => {
    setForm((f) => {
      const items = f.items
        .filter((_, i) => i !== idx)
        .map((it, i) => ({ ...it, colli_number: i + 1 }));
      // Garandeer dat er minstens 1 colli is
      return { ...f, items: items.length > 0 ? items : [newColli(0)] };
    });
    setConfirmDeleteIdx(null);
  };

  const requestDeleteItem = (idx) => {
    const it = form.items[idx];
    if (isItemEmpty(it)) {
      deleteItem(idx);
    } else {
      setConfirmDeleteIdx(idx);
    }
  };

  // ====== Submit ======
  const submit = async () => {
    if (!allDone) {
      toast.error("Vul eerst alle vier blokken volledig in voordat je registreert.");
      return;
    }
    // P0 SPRINT — override detection na klant-autofill
    const recDiffs = detectAddressOverrides(recipientSourceCustomer, form.recipient);
    const sndDiffs = detectAddressOverrides(senderSourceCustomer, form.sender);
    if (recDiffs.length > 0 || sndDiffs.length > 0) {
      const chosen = recDiffs.length > 0
        ? { customer: recipientSourceCustomer, diffs: recDiffs, party: "recipient" }
        : { customer: senderSourceCustomer, diffs: sndDiffs, party: "sender" };
      setOverrideDialog(chosen);
      return;
    }
    await proceedCreate();
  };

  const proceedCreate = async () => {
    if (preAlert && !preAlertConfirmed) {
      toast.error("Bevestig eerst de klantgegevens, waarde en werkelijke warehouse-metingen.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        label_type: "warehouse_package",
        service_type: form.service_type,
        payment_status: "not_paid",
        sender: stripParty(form.sender),
        recipient: stripParty(form.recipient),
        customer_id: form.customer_id || null,
        shipment_details: {
          destination: form.destination.trim() || form.recipient.country || "",
          customer_note: form.customer_note.trim() || null,
          internal_note: form.internal_note.trim() || null,
          currency: form.currency || "EUR",
          measurement: form.measurement,
          items: form.items.map((it, idx) => ({
            colli_number: idx + 1,
            description: it.description?.trim() || null,
            quantity: parseInt(num(it.quantity) || 1, 10) || 1,
            weight_kg: num(it.weight_kg),
            length_cm: num(it.length_cm),
            width_cm: num(it.width_cm),
            height_cm: num(it.height_cm),
            declared_value: num(it.declared_value),
            packaging_type: it.packaging_type || null,
          })),
        },
        warehouse_hub: form.warehouse_hub.trim(),
        agency: form.agency.trim() || null,
        linked_external_tracking_number: form.linked_external_tracking_number.trim() || null,
        // Herbruikbare ontvangers — de zending houdt altijd zijn eigen
        // snapshot; deze vlaggen bepalen enkel het adresboek van de klant.
        save_recipient_to_address_book: !!(saveRecipient && form.customer_id),
        recipient_book_id: recipientBookId || null,
      };
      const r = preAlert
        ? await adminApi.post(`/admin/pre-alerts/${preAlert.id}/receive`, { revision: preAlert.revision, warehouse_confirmed: preAlertConfirmed, shipment: payload })
        : await adminApi.post("/admin/shipping-labels", payload);
      setSuccess(r.data);
      toast.success(`Zending ${r.data.tracking_number} geregistreerd`);
      onCreated?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Zending aanmaken mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-5xl p-0 flex flex-col bg-[#F4F5F7]"
        data-testid="warehouse-package-sheet"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Nieuwe zending registreren</SheetTitle>
          <SheetDescription>
            Voeg een fysieke zending toe aan het magazijn. Het W-nummer wordt
            automatisch toegekend zodra alle vier de blokken zijn ingevuld.
          </SheetDescription>
        </SheetHeader>
        {success ? (
          <SuccessState
            pkg={success}
            onAnother={reset}
            onOpenDetail={() => { onCreated?.(success, "open"); onClose?.(); }}
            onClose={handleClose}
          />
        ) : (
          <>
            <div className="bg-gradient-to-r from-[#082c50] to-[#0a3a6b] text-white px-6 py-4 flex items-start justify-between gap-3 flex-shrink-0">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
                  <Warehouse className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h1 className="font-chivo font-black text-xl tracking-tight">Nieuwe zending registreren</h1>
                  <p className="text-xs text-blue-100 mt-0.5">
                    Voeg een zending toe aan het magazijn. W-nummer wordt automatisch toegekend.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="text-white/70 hover:text-white"
                data-testid="warehouse-form-close"
                aria-label="Sluiten"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <LiveSummary
              completed={completedBlocks}
              cbm={totals.cbm}
              volWeight={totals.vw}
              weight={totals.weight}
              pieceCount={totals.pieces}
              totalValue={totals.value}
              currency={form.currency}
              allDone={allDone}
              transportMode={totals.transportMode}
              divisor={totals.divisor}
              chargeable={totals.chargeable}
              chargeableBasis={totals.chargeableBasis}
            />

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <Block index={1} title="Zending-basis"
                     description="Magazijn, agentschap en diensttype"
                     icon={PackageIcon} done={status.basisDone}
                     testId="warehouse-block-basis">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Magazijn / hub *">
                    <Input ref={firstFieldRef} value={form.warehouse_hub}
                           onChange={(e) => setForm((f) => ({ ...f, warehouse_hub: e.target.value }))}
                           placeholder="Rotterdam HQ" className="rounded-none h-10"
                           data-testid="warehouse-field-hub" />
                  </Field>
                  <Field label="Agentschap (optioneel)">
                    <Input value={form.agency}
                           onChange={(e) => setForm((f) => ({ ...f, agency: e.target.value }))}
                           placeholder="5999Cargo NL" className="rounded-none h-10"
                           data-testid="warehouse-field-agency" />
                  </Field>
                  <Field label="Diensttype *">
                    <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
                      <SelectTrigger className="rounded-none h-10" data-testid="warehouse-field-service">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_TYPES.map((s) => (
                          <SelectItem key={s.key} value={s.key} data-testid={`warehouse-service-${s.key}`}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Extern W-nummer / trackingnummer (optioneel)">
                    <div className="flex gap-2">
                      <Input value={form.linked_external_tracking_number}
                             onChange={(e) => updateExternalTracking(e.target.value)}
                             placeholder="W523518422761"
                             className="rounded-none h-10 font-mono text-xs uppercase flex-1"
                             data-testid="warehouse-field-external-w" />
                      <ScanButton
                        iconOnly
                        testId="warehouse-scan-external-w"
                        title="Scan extern W-nummer / barcode"
                        onResult={updateExternalTracking}
                      />
                    </div>
                  </Field>
                </div>
                <WarehousePreAlertMatch tracking={form.linked_external_tracking_number} selected={preAlert}
                  initialPrealertId={initialPrealertId} customerId={form.customer_id}
                  onSelect={selectPreAlert} confirmed={preAlertConfirmed} onConfirm={setPreAlertConfirmed} onClear={clearPreAlert} />
              </Block>

              <Block index={2} title="Afzender"
                     description="Wie levert het pakket aan? (Klant koppelen mag optioneel)"
                     icon={UserIcon} done={status.senderDone}
                     testId="warehouse-block-sender">
                <CustomerSearchField
                  testIdPrefix="warehouse-sender-customer"
                  onSelect={(c) => {
                    setForm((f) => ({ ...f, customer_id: c.id || c.customer_id }));
                    setSender(customerToParty(c));
                    setSenderSourceCustomer(c);
                    // Batch 3 · Fase 5 — stale-reset: nieuwe klant = terug naar primair contact.
                    setSenderContactKey("primary");
                    toast.success(`Klant ${customerDisplayName(c)} gekoppeld als afzender`);
                  }}
                />
                <ContactPersonSelector
                  customer={senderSourceCustomer}
                  value={senderContactKey}
                  onSelect={(key, contact) => {
                    setSenderContactKey(key);
                    setForm((f) => ({ ...f, sender: applyContactToParty(f.sender, contact) }));
                  }}
                  label="Contactpersoon (afzender)"
                  testIdPrefix="warehouse-sender-contact"
                />
                <PartyFields party={form.sender} onChange={setSender} testIdPrefix="warehouse-sender" />
              </Block>

              <Block index={3} title="Ontvanger"
                     description="Naar wie gaat de zending? (Klant koppelen mag optioneel)"
                     icon={MapPin} done={status.recipientDone}
                     testId="warehouse-block-recipient">
                <CustomerSearchField
                  testIdPrefix="warehouse-recipient-customer"
                  onSelect={(c) => {
                    setForm((f) => ({ ...f, customer_id: c.id || c.customer_id }));
                    setRecipient(customerToParty(c));
                    setRecipientSourceCustomer(c);
                    // Batch 3 · Fase 5 — stale-reset naar primair contact.
                    setRecipientContactKey("primary");
                    toast.success(`Klant ${customerDisplayName(c)} gekoppeld als ontvanger`);
                  }}
                />
                <ContactPersonSelector
                  customer={recipientSourceCustomer}
                  value={recipientContactKey}
                  onSelect={(key, contact) => {
                    setRecipientContactKey(key);
                    setForm((f) => ({ ...f, recipient: applyContactToParty(f.recipient, contact) }));
                  }}
                  label="Contactpersoon (ontvanger)"
                  testIdPrefix="warehouse-recipient-contact"
                />
                {/* Sprint jun 2026 — adresboek van de gekoppelde klant */}
                <div className="mt-2">
                  <RecipientAddressBookPicker
                    customerId={form.customer_id}
                    testIdPrefix="warehouse-recipient-book"
                    onSelect={(r) => {
                      setRecipient(recipientBookToParty(r));
                      setRecipientBookId(r.id);
                      setRecipientSourceCustomer(null);
                      toast.success(`Ontvanger ${r.name} ingevuld uit adresboek`);
                    }}
                  />
                </div>
                <PartyFields party={form.recipient} onChange={setRecipient} testIdPrefix="warehouse-recipient" nameRequired />
                {form.customer_id && (
                  <label className="mt-2 flex items-start gap-2 text-xs cursor-pointer"
                         data-testid="warehouse-recipient-save-label">
                    <input type="checkbox" checked={saveRecipient} className="mt-0.5"
                           onChange={(e) => setSaveRecipient(e.target.checked)}
                           data-testid="warehouse-recipient-save" />
                    <span>
                      <strong>Ontvanger bewaren</strong> in het adresboek van deze klant —
                      volgende keer direct te kiezen. De zending houdt altijd zijn eigen
                      gegevens.
                    </span>
                  </label>
                )}
                <div className="mt-3">
                  <Field label="Bestemming (vrij tekst, optioneel)">
                    <Input value={form.destination}
                           onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))}
                           placeholder="Willemstad, Curaçao" className="rounded-none h-10"
                           data-testid="warehouse-field-destination" />
                  </Field>
                </div>
              </Block>

              <Block index={4} title="Colli & inhoud"
                     description={`${form.items.length} ${form.items.length === 1 ? "colli" : "colli"} · CBM en gewicht worden live berekend`}
                     icon={Box} done={status.contentDone}
                     testId="warehouse-block-content">
                <div className="space-y-3">
                  {form.items.map((it, idx) => (
                    <ColliRow key={it._local_id || idx}
                              item={it} index={idx}
                              serviceType={form.service_type}
                              onChange={(patch) => updateItem(idx, patch)}
                              onDelete={() => requestDeleteItem(idx)}
                              showDelete={form.items.length > 1 || !isItemEmpty(it)} />
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                  <Button type="button" variant="outline" size="sm"
                          onClick={addColli}
                          className="rounded-none text-xs h-9"
                          data-testid="warehouse-add-colli">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Colli toevoegen
                  </Button>
                  <Button type="button" variant="outline" size="sm"
                          onClick={copyPrevColli}
                          disabled={form.items.length === 0}
                          className="rounded-none text-xs h-9"
                          data-testid="warehouse-copy-prev-colli">
                    <Copy className="w-3.5 h-3.5 mr-1" /> Vorige colli kopiëren
                  </Button>
                  <Button type="button" variant="outline" size="sm"
                          onClick={() => setConfirmCopyAll(true)}
                          disabled={form.items.length < 2}
                          className="rounded-none text-xs h-9"
                          data-testid="warehouse-copy-to-all">
                    <ClipboardCopy className="w-3.5 h-3.5 mr-1" /> Kopieer naar alle colli
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                  <Field label="Currency">
                    <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                      <SelectTrigger className="rounded-none h-10" data-testid="warehouse-field-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Meting">
                    <Select value={form.measurement} onValueChange={(v) => setForm((f) => ({ ...f, measurement: v }))}>
                      <SelectTrigger className="rounded-none h-10" data-testid="warehouse-field-measurement">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(MEASUREMENT_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <Field label="Klantnotitie (zichtbaar in tracking)">
                    <Textarea rows={2} value={form.customer_note}
                              onChange={(e) => setForm((f) => ({ ...f, customer_note: e.target.value }))}
                              placeholder="Bv. fragiel — voorzichtig behandelen"
                              className="rounded-none"
                              data-testid="warehouse-field-customer-note" />
                  </Field>
                  <Field label="Interne notitie (alleen intern)">
                    <Textarea rows={2} value={form.internal_note}
                              onChange={(e) => setForm((f) => ({ ...f, internal_note: e.target.value }))}
                              placeholder="Bv. ligt achter in vak 4-B"
                              className="rounded-none"
                              data-testid="warehouse-field-internal-note" />
                  </Field>
                </div>
              </Block>

              <div className="pb-2" />
            </div>

            <div className="border-t border-gray-200 bg-white px-6 py-3 flex items-center justify-between flex-shrink-0">
              <div className="text-xs text-gray-500" data-testid="warehouse-form-block-status">
                {completedBlocks} van 4 blokken compleet
                {allDone ? (
                  <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 font-semibold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Klaar om te registreren
                  </span>
                ) : (
                  <span className="ml-2 text-amber-700">
                    — nog nodig: {[
                      !status.basisDone && "Basis (hub + dienst)",
                      !status.senderDone && "Afzender (naam + telefoon/e-mail)",
                      !status.recipientDone && "Ontvanger (naam)",
                      !status.contentDone && "Colli (elke colli gewicht + L\u00d7B\u00d7H)",
                    ].filter(Boolean).join(" · ")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleClose} disabled={busy}
                        className="rounded-none text-xs h-10"
                        data-testid="warehouse-form-cancel">
                  Annuleren
                </Button>
                <Button onClick={submit} disabled={busy || !allDone}
                        className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none text-xs font-bold uppercase h-10 px-5"
                        data-testid="warehouse-form-submit">
                  {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                  {busy ? "Registreren…" : "Registreer zending"}
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>

      {/* Confirm "kopieer naar alle colli" */}
      <AlertDialog open={confirmCopyAll} onOpenChange={(o) => !o && setConfirmCopyAll(false)}>
        <AlertDialogContent data-testid="warehouse-confirm-copy-all">
          <AlertDialogHeader>
            <AlertDialogTitle>Eerste colli naar alle colli kopiëren?</AlertDialogTitle>
            <AlertDialogDescription>
              Dit overschrijft de huidige waarden van alle colli-regels met
              de gegevens van Colli 1. Notities blijven op zending-niveau.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="warehouse-confirm-copy-all-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={copyToAll} data-testid="warehouse-confirm-copy-all-ok">
              Ja, kopieer naar alle colli
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete colli */}
      <AlertDialog open={confirmDeleteIdx !== null} onOpenChange={(o) => !o && setConfirmDeleteIdx(null)}>
        <AlertDialogContent data-testid="warehouse-confirm-delete-colli">
          <AlertDialogHeader>
            <AlertDialogTitle>Colli verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet u zeker dat u Colli {(confirmDeleteIdx ?? 0) + 1} wilt verwijderen?
              De ingevulde gegevens van deze regel gaan verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="warehouse-confirm-delete-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteItem(confirmDeleteIdx)} data-testid="warehouse-confirm-delete-ok">
              Ja, verwijder colli
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* P0 SPRINT (Aug 2026) — Override confirm dialog na klant-autofill */}
      {overrideDialog && (
        <AddressOverrideDialog
          open={!!overrideDialog}
          onOpenChange={(v) => { if (!v) setOverrideDialog(null); }}
          customerId={overrideDialog.customer?.customer_id || overrideDialog.customer?.id}
          customerName={overrideDialog.customer?.display_name || overrideDialog.customer?.company_name}
          diffs={overrideDialog.diffs}
          onKeepShipmentOnly={async () => {
            const wasRec = overrideDialog.party === "recipient";
            setOverrideDialog(null);
            if (wasRec) setRecipientSourceCustomer(null);
            else setSenderSourceCustomer(null);
            await proceedCreate();
          }}
        />
      )}
    </Sheet>
  );
}

// ============================================================
// Customer helpers — canonieke Enterprise Entity Picker
// wrapper (CustomerSearchField) + shape helpers (customerToParty,
// customerDisplayName) worden geïmporteerd uit WarehouseFormShared.
// Single source of truth voor klant-zoeken in het platform.
// ============================================================

// ============================================================
// Live summary
// ============================================================

function LiveSummary({ completed, cbm, volWeight, weight, pieceCount, totalValue, currency, allDone,
                       transportMode = "default", divisor = volumetricDivisorForService(), chargeable = 0,
                       chargeableBasis = "gross_weight" }) {
  const pct = Math.round((completed / 4) * 100);
  return (
    <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label="Voortgang" value={`${pct}%`} sub={`${completed}/4 blokken`} accent={allDone ? "green" : "blue"} testId="warehouse-live-progress" />
        <Stat label="Totaal colli" value={pieceCount > 0 ? String(pieceCount) : "—"} sub="alle regels" testId="warehouse-live-pieces" />
        <Stat label="Totaal gewicht" value={weight > 0 ? `${fmtNum(weight, 2)} kg` : "—"} sub="bruto" testId="warehouse-live-weight" />
        <Stat label="Totaal CBM" value={cbm > 0 ? fmtNum(cbm, 4) : "—"} sub="m³" accent="purple" testId="warehouse-live-cbm" />
        <Stat label="Vol. gewicht" value={volWeight > 0 ? `${fmtNum(volWeight, 2)}` : "—"} sub={`kg (${TRANSPORT_MODE_LABELS[transportMode] || "standaard"} · /${divisor})`} accent="amber" testId="warehouse-live-vol-weight" />
      </div>
      {chargeable > 0 && (
        <div className="mt-2 text-[11px] text-gray-500 font-mono" data-testid="warehouse-live-chargeable">
          Belastbaar gewicht: <span className="text-[#082c50] font-bold">{fmtNum(chargeable, 2)} kg</span>
          {" "}({chargeableBasis === "volumetric_weight" ? "volumetrisch" : "werkelijk"} is leidend)
        </div>
      )}
      {totalValue > 0 && (
        <div className="mt-2 text-[11px] text-gray-500 font-mono">
          Aangegeven waarde totaal: <span className="text-[#082c50] font-bold">{currency} {fmtNum(totalValue, 2)}</span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, accent = "default", testId }) {
  const accentMap = {
    default: "border-gray-200 bg-gray-50 text-[#082c50]",
    blue:    "border-blue-200 bg-blue-50 text-blue-900",
    green:   "border-emerald-200 bg-emerald-50 text-emerald-900",
    purple:  "border-purple-200 bg-purple-50 text-purple-900",
    amber:   "border-amber-200 bg-amber-50 text-amber-900",
  };
  return (
    <div className={`border ${accentMap[accent]} px-3 py-2 rounded-md`} data-testid={testId}>
      <div className="text-[9px] uppercase font-bold tracking-wide opacity-70">{label}</div>
      <div className="font-chivo font-black text-lg leading-tight">{value}</div>
      <div className="text-[10px] opacity-60">{sub}</div>
    </div>
  );
}

// ============================================================
// Block wrapper
// ============================================================

function Block({ index, title, description, icon: Icon, done, children, testId }) {
  return (
    <section
      className={`bg-white border ${done ? "border-emerald-300 shadow-[0_0_0_1px_rgba(16,185,129,0.15)]" : "border-gray-200"} rounded-md overflow-hidden transition-colors`}
      data-testid={testId}
      data-done={done ? "true" : "false"}
    >
      <header className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${done ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-gray-300 text-gray-500"}`}>
          {done ? <CheckCircle2 className="w-5 h-5" /> : <span className="font-chivo font-black text-sm">{index}</span>}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-[#082c50]" />
            <h3 className="font-chivo font-bold text-sm text-[#082c50]">{title}</h3>
          </div>
          <p className="text-[11px] text-gray-500 leading-tight">{description}</p>
        </div>
        {done && (
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-emerald-700 font-bold uppercase tracking-wide"
                data-testid={`${testId}-done-badge`}>
            <CheckCircle2 className="w-3 h-3" /> Compleet
          </span>
        )}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

// ============================================================
// Colli row
// ============================================================

function ColliRow({ item, index, onChange, onDelete, showDelete, serviceType }) {
  const cbm = useMemo(() => calcItemCbm(item), [item]);
  const volW = useMemo(() => calcItemVolWeight(item, serviceType), [item, serviceType]);
  const filled = num(item.weight_kg) > 0 && num(item.length_cm) > 0 && num(item.width_cm) > 0 && num(item.height_cm) > 0;
  return (
    <div className={`border ${filled ? "border-emerald-200" : "border-gray-200"} bg-white rounded-md p-3`}
         data-testid={`warehouse-colli-row-${index}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold ${filled ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-700"}`}>
            {index + 1}
          </span>
          <span className="font-chivo font-bold text-sm text-[#082c50]">Colli {index + 1}</span>
          {filled && (
            <span className="text-[10px] text-emerald-700 font-bold uppercase tracking-wide">Ingevuld</span>
          )}
        </div>
        {showDelete && (
          <button type="button" onClick={onDelete}
                  className="text-gray-400 hover:text-red-600 p-1"
                  title="Verwijder deze colli"
                  data-testid={`warehouse-colli-delete-${index}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
        <div className="sm:col-span-5">
          <Field label="Omschrijving">
            <Input value={item.description || ""}
                   onChange={(e) => onChange({ description: e.target.value })}
                   placeholder="Bv. doos kleding"
                   className="rounded-none h-9 text-xs"
                   data-testid={`warehouse-colli-${index}-description`} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Aantal">
            <Input type="number" inputMode="numeric" min={1}
                   value={item.quantity}
                   onChange={(e) => onChange({ quantity: e.target.value })}
                   className="rounded-none h-9 text-xs"
                   data-testid={`warehouse-colli-${index}-quantity`} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Gewicht (kg) *">
            <Input type="number" inputMode="decimal" step="0.01" min={0}
                   value={item.weight_kg}
                   onChange={(e) => onChange({ weight_kg: e.target.value })}
                   className="rounded-none h-9 text-xs"
                   data-testid={`warehouse-colli-${index}-weight`} />
          </Field>
        </div>
        <div className="sm:col-span-3">
          <Field label="Verpakkingstype">
            <Select value={item.packaging_type || "box"} onValueChange={(v) => onChange({ packaging_type: v })}>
              <SelectTrigger className="rounded-none h-9 text-xs" data-testid={`warehouse-colli-${index}-packaging`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PACKAGING_TYPES.map((p) => (
                  <SelectItem key={p.key} value={p.key} data-testid={`warehouse-colli-${index}-pack-${p.key}`}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
        <Field label="Lengte (cm) *">
          <Input type="number" inputMode="decimal" step="0.1" min={0}
                 value={item.length_cm}
                 onChange={(e) => onChange({ length_cm: e.target.value })}
                 className="rounded-none h-9 text-xs"
                 data-testid={`warehouse-colli-${index}-length`} />
        </Field>
        <Field label="Breedte (cm) *">
          <Input type="number" inputMode="decimal" step="0.1" min={0}
                 value={item.width_cm}
                 onChange={(e) => onChange({ width_cm: e.target.value })}
                 className="rounded-none h-9 text-xs"
                 data-testid={`warehouse-colli-${index}-width`} />
        </Field>
        <Field label="Hoogte (cm) *">
          <Input type="number" inputMode="decimal" step="0.1" min={0}
                 value={item.height_cm}
                 onChange={(e) => onChange({ height_cm: e.target.value })}
                 className="rounded-none h-9 text-xs"
                 data-testid={`warehouse-colli-${index}-height`} />
        </Field>
        <Field label="Waarde / stuk">
          <Input type="number" inputMode="decimal" step="0.01" min={0}
                 value={item.declared_value}
                 onChange={(e) => onChange({ declared_value: e.target.value })}
                 placeholder="0,00"
                 className="rounded-none h-9 text-xs"
                 data-testid={`warehouse-colli-${index}-value`} />
        </Field>
      </div>
      <div className="mt-2 flex items-center gap-3 text-[10px] text-gray-500 font-mono"
           data-testid={`warehouse-colli-${index}-live`}>
        <span>CBM: <span className="text-[#082c50] font-bold">{fmtNum(cbm, 4)} m³</span></span>
        <span>·</span>
        <span>Vol. gewicht: <span className="text-[#082c50] font-bold">{fmtNum(volW, 2)} kg</span></span>
      </div>
    </div>
  );
}

// ============================================================
// Party fields
// ============================================================

function PartyFields({ party, onChange, testIdPrefix, nameRequired = false }) {
  const set = (k, v) => onChange({ [k]: v });
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={`Naam ${nameRequired ? "*" : ""}`}>
          <Input value={party.name || ""} onChange={(e) => set("name", e.target.value)}
                 className="rounded-none h-10" data-testid={`${testIdPrefix}-name`} />
        </Field>
        <Field label="Bedrijfsnaam">
          <Input value={party.company || ""} onChange={(e) => set("company", e.target.value)}
                 className="rounded-none h-10" data-testid={`${testIdPrefix}-company`} />
        </Field>
        <Field label="Telefoon">
          <Input value={party.phone || ""} onChange={(e) => set("phone", e.target.value)}
                 placeholder="+31..." className="rounded-none h-10" data-testid={`${testIdPrefix}-phone`} />
        </Field>
        <Field label="WhatsApp">
          <Input value={party.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)}
                 placeholder="+31..." className="rounded-none h-10" data-testid={`${testIdPrefix}-whatsapp`} />
        </Field>
        <Field label="E-mail">
          <Input type="email" value={party.email || ""} onChange={(e) => set("email", e.target.value)}
                 className="rounded-none h-10" data-testid={`${testIdPrefix}-email`} />
        </Field>
        <Field label="Land">
          <Input value={party.country || ""} onChange={(e) => set("country", e.target.value)}
                 className="rounded-none h-10" data-testid={`${testIdPrefix}-country`} />
        </Field>
      </div>
      <div className="grid grid-cols-12 gap-3 mt-3">
        <div className="col-span-12 sm:col-span-6">
          <Field label="Straat">
            <Input value={party.street || ""} onChange={(e) => set("street", e.target.value)}
                   className="rounded-none h-10" data-testid={`${testIdPrefix}-street`} />
          </Field>
        </div>
        <div className="col-span-6 sm:col-span-2">
          <Field label="Huisnr.">
            <Input value={party.house_number || ""} onChange={(e) => set("house_number", e.target.value)}
                   className="rounded-none h-10" data-testid={`${testIdPrefix}-house_number`} />
          </Field>
        </div>
        <div className="col-span-6 sm:col-span-4">
          <Field label="Toev.">
            <Input value={party.house_number_addition || ""} onChange={(e) => set("house_number_addition", e.target.value)}
                   className="rounded-none h-10" data-testid={`${testIdPrefix}-house_number_addition`} />
          </Field>
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Field label="Postcode">
            <Input value={party.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)}
                   className="rounded-none h-10" data-testid={`${testIdPrefix}-postal_code`} />
          </Field>
        </div>
        <div className="col-span-6 sm:col-span-9">
          <Field label="Plaats">
            <Input value={party.city || ""} onChange={(e) => set("city", e.target.value)}
                   className="rounded-none h-10" data-testid={`${testIdPrefix}-city`} />
          </Field>
        </div>
      </div>
    </>
  );
}

// ============================================================
// Success state
// ============================================================

function SuccessState({ pkg, onAnother, onOpenDetail, onClose }) {
  const [consDialogOpen, setConsDialogOpen] = useState(false);

  return (
    <div className="flex flex-col h-full bg-white" data-testid="warehouse-success-state">
      <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white px-6 py-8 text-center flex-shrink-0">
        <div className="inline-flex w-16 h-16 rounded-full bg-white/15 border border-white/30 items-center justify-center mb-3">
          <CheckCircle2 className="w-9 h-9" />
        </div>
        <h2 className="font-chivo font-black text-2xl tracking-tight">Zending geregistreerd</h2>
        <p className="text-sm text-emerald-100 mt-1">Uw zending staat nu klaar in ons magazijn.</p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="bg-[#082c50] text-white px-5 py-5 text-center rounded-md mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-blue-200 mb-1">W-nummer</div>
          <div className="font-mono font-black text-3xl tracking-wider" data-testid="warehouse-success-wnumber">
            {pkg.tracking_number}
          </div>
        </div>

        <div className="flex items-center gap-2 justify-center flex-wrap mb-5">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-900 border border-emerald-200 rounded-full"
                data-testid="warehouse-success-status">
            <CheckCircle2 className="w-3.5 h-3.5" /> In magazijn
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold uppercase tracking-wide bg-amber-100 text-amber-900 border border-amber-200 rounded-full"
                data-testid="warehouse-success-not-consolidated">
            <Circle className="w-3 h-3" /> Niet geconsolideerd
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center mb-6">
          <SuccessStat label="Colli" value={pkg.shipment_details?.piece_count ?? "—"} />
          <SuccessStat label="Gewicht" value={pkg.shipment_details?.weight_kg ? `${pkg.shipment_details.weight_kg} kg` : "—"} />
          <SuccessStat label="Bestemming" value={pkg.destination_country_name || pkg.shipment_details?.destination || pkg.recipient?.country || "—"} />
          <SuccessStat label="Hub" value={pkg.warehouse_hub || "—"} />
        </div>

        <div className="space-y-2">
          <ActionButton icon={Printer} title="Label printen"
                        description="Open PDF in nieuw tabblad"
                        onClick={() => printLabel(pkg)}
                        testId="warehouse-action-print" primary />
          <ActionButton icon={Layers} title="Toevoegen aan consolidatie"
                        description="Koppel direct aan een open consolidatie"
                        onClick={() => setConsDialogOpen(true)}
                        testId="warehouse-action-add-consolidation" />
          <ActionButton icon={Plus} title="Nog een zending registreren"
                        description="Reset formulier en start opnieuw"
                        onClick={onAnother}
                        testId="warehouse-action-another" />
          <ActionButton icon={Eye} title="Zendingdetail openen"
                        description="Bekijk volledige zending-informatie"
                        onClick={onOpenDetail}
                        testId="warehouse-action-open-detail" />
        </div>
      </div>

      <div className="border-t border-gray-200 bg-white px-6 py-3 flex justify-end flex-shrink-0">
        <Button variant="outline" onClick={onClose} className="rounded-none text-xs h-9" data-testid="warehouse-success-close">
          Sluiten
        </Button>
      </div>

      {consDialogOpen && (
        <AddToConsolidationDialog pkg={pkg} onClose={() => setConsDialogOpen(false)} />
      )}
    </div>
  );
}

function SuccessStat({ label, value }) {
  return (
    <div className="bg-gray-50 border border-gray-200 px-2 py-2 rounded-md">
      <div className="text-[9px] uppercase tracking-wide font-bold text-gray-500">{label}</div>
      <div className="font-chivo font-bold text-sm text-[#082c50] truncate" title={String(value)}>{value}</div>
    </div>
  );
}

function ActionButton({ icon: Icon, title, description, onClick, testId, primary = false }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
            className={`w-full flex items-center gap-3 px-4 py-3 border rounded-md text-left transition-all ${primary
              ? "bg-[#0055FF] hover:bg-[#0044CC] border-[#0055FF] text-white"
              : "bg-white hover:bg-gray-50 border-gray-200 text-[#082c50]"}`}>
      <Icon className={`w-5 h-5 ${primary ? "text-white" : "text-[#0055FF]"} flex-shrink-0`} />
      <div className="flex-1">
        <div className="font-chivo font-bold text-sm">{title}</div>
        <div className={`text-[11px] ${primary ? "text-blue-100" : "text-gray-500"}`}>{description}</div>
      </div>
      <ChevronRight className="w-4 h-4 opacity-50" />
    </button>
  );
}

// ============================================================
// Add to consolidation dialog
// ============================================================

function AddToConsolidationDialog({ pkg, onClose }) {
  const [loading, setLoading] = useState(false);
  const [consolidations, setConsolidations] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [blockedError, setBlockedError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await adminApi.get("/admin/consolidations", { params: { status: "open", limit: 50 } });
        if (!cancelled) setConsolidations(r.data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Open consolidaties kunnen niet worden geladen");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async () => {
    if (!selectedId) { toast.error("Selecteer eerst een consolidatie"); return; }
    setSubmitting(true);
    setBlockedError(null);
    try {
      const r = await adminApi.post(`/admin/consolidations/${selectedId}/shipments`, {
        w_numbers: [pkg.tracking_number],
      });
      const added = (r.data.added || []).length;
      const errs = r.data.errors || [];
      if (added > 0) {
        const cons = consolidations.find((c) => c.id === selectedId);
        setDone({ number: cons?.consolidation_number || selectedId });
        toast.success(`Zending toegevoegd aan ${cons?.consolidation_number || "consolidatie"}`);
      } else if (errs.length > 0) {
        setBlockedError(errs[0]);
      } else {
        toast.error("Zending kon niet worden toegevoegd.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toevoegen mislukt");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="warehouse-add-consolidation-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-[#082c50] flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#0055FF]" /> Toevoegen aan consolidatie
          </DialogTitle>
          <DialogDescription>
            Zending <span className="font-mono font-bold">{pkg.tracking_number}</span> koppelen aan een open consolidatie.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-4 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <p className="font-chivo font-bold text-[#082c50]">Toegevoegd aan {done.number}</p>
            <p className="text-xs text-gray-500 mt-1">De consolidatie-totalen zijn direct bijgewerkt.</p>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            {blockedError && (
              <div className="bg-red-50 border border-red-200 px-3 py-2 rounded-md text-xs text-red-900"
                   data-testid="warehouse-add-consolidation-error">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-bold">Toevoegen geblokkeerd</div>
                    <div className="mt-0.5">{blockedError.message}</div>
                  </div>
                </div>
              </div>
            )}
            {loading ? (
              <div className="text-center py-6 text-gray-500">
                <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Laden…
              </div>
            ) : consolidations.length === 0 ? (
              <div className="text-sm text-gray-600 bg-amber-50 border border-amber-200 p-3 rounded-md flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-amber-900">Geen open consolidaties</div>
                  <div className="text-xs text-amber-800 mt-0.5">Maak eerst een consolidatie aan via de Consolidaties-sectie.</div>
                </div>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto"
                   data-testid="warehouse-consolidation-options">
                {consolidations.map((c) => {
                  const isSel = selectedId === c.id;
                  return (
                    <button key={c.id} type="button"
                            onClick={() => { setSelectedId(c.id); setBlockedError(null); }}
                            data-testid={`warehouse-consolidation-option-${c.id}`}
                            className={`w-full flex items-center gap-3 px-3 py-2 border text-left transition-colors ${isSel ? "border-[#0055FF] bg-blue-50" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
                      <div className={`w-4 h-4 rounded-full border-2 ${isSel ? "border-[#0055FF] bg-[#0055FF]" : "border-gray-300"}`} />
                      <div className="flex-1">
                        <div className="font-mono font-bold text-xs text-[#082c50]">{c.consolidation_number}</div>
                        <div className="text-[11px] text-gray-500">
                          {c.departure_hub_label} <ArrowRight className="inline w-3 h-3 mx-1" /> {c.destination_hub_label}
                        </div>
                      </div>
                      <div className="text-[10px] text-gray-500 text-right">
                        {c.shipment_count || 0} pakk.<br />
                        {(c.totals_summary?.weight_kg ?? 0).toFixed(1)} kg
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {done ? (
            <Button onClick={onClose} className="rounded-none text-xs h-9 bg-[#0055FF] text-white"
                    data-testid="warehouse-add-consolidation-done">
              Klaar
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={submitting}
                      className="rounded-none text-xs h-9"
                      data-testid="warehouse-add-consolidation-cancel">
                Annuleren
              </Button>
              <Button onClick={submit} disabled={!selectedId || submitting || loading || consolidations.length === 0}
                      className="rounded-none text-xs h-9 bg-[#0055FF] text-white"
                      data-testid="warehouse-add-consolidation-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Toevoegen
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Helpers
// ============================================================

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function stripParty(p) {
  const out = {};
  Object.entries(p || {}).forEach(([k, v]) => {
    const s = typeof v === "string" ? v.trim() : v;
    if (s !== "" && s !== null && s !== undefined) out[k] = s;
  });
  return out;
}

export function printLabel(pkg) {
  const token = localStorage.getItem("cargo_admin_token");
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/shipping-labels/${pkg.id}/pdf`;
  const win = window.open("about:blank", "_blank");
  if (win) {
    win.document.write(`<title>Label ${pkg.tracking_number}</title><p style="font-family:sans-serif;color:#082c50">Label wordt geopend…</p>`);
  }
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => {
      if (!r.ok) throw new Error("Label kon niet worden gedownload");
      return r.blob();
    })
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      if (win) win.location.href = blobUrl;
      else window.open(blobUrl, "_blank");
    })
    .catch((e) => {
      if (win) win.close();
      toast.error(e.message || "Label openen mislukt");
    });
}
